/**
 * ClipLoader — Lazy AnimationClip cache.
 *
 * Each animation clip lives in its own GLB file (anim_run.glb,
 * anim_football_stance.glb, etc.) so we only download what the current
 * scene actually uses. A coverage scene with no QB drop never pays for
 * the dropback animation; a route-running scene never downloads the
 * 3-point stance clip.
 *
 * # Caching contract
 *
 * - One in-flight Promise per clip id (de-duplicates concurrent calls).
 * - Permanent cache after first resolve.
 * - The cached value is the raw THREE.AnimationClip — callers feed it
 *   into per-player AnimationMixer.clipAction(clip).
 *
 * # Skeleton compatibility
 *
 * All Mixamo animation clips share the standard `mixamorig*` bone names,
 * so a clip downloaded with one character (X-Bot) plays cleanly on any
 * other character that uses the same skeleton (Vanguard, Synty Sports
 * Pack characters, etc.). When we eventually add a non-Mixamo mesh, we'd
 * either retarget at load time (SkeletonUtils.retargetClip) or add a
 * retargeting table to the manifest.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { retargetClip } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { CLIPS, MESHES, resolveClip, resolveMesh, resolveVerb, assetUrl } from './AssetManifest';

const _gltfCache = {};
const _gltfPromises = {};
const _clipCache = {};
// Per (clipId, meshId) cache for retargeted clips. Retargeting is a
// one-time mathematical operation — we never want to redo it for every
// player on every keyframe. Key is `${clipId}::${meshId}`.
const _retargetedClipCache = {};

function _loadGltfOnce(filename) {
  if (_gltfCache[filename]) return Promise.resolve(_gltfCache[filename]);
  if (_gltfPromises[filename]) return _gltfPromises[filename];
  _gltfPromises[filename] = new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.load(
      assetUrl(filename),
      (gltf) => { _gltfCache[filename] = gltf; resolve(gltf); },
      undefined,
      (err) => { delete _gltfPromises[filename]; reject(err); },
    );
  });
  return _gltfPromises[filename];
}

/**
 * Load an AnimationClip by its manifest id (e.g. "sprint", "football_stance").
 * Returns the same THREE.AnimationClip instance on every call after the
 * first — safe to feed to many AnimationMixer.clipAction() calls because
 * AnimationMixer wraps the clip in a per-mixer Action.
 */
export async function loadClip(clipId) {
  if (_clipCache[clipId]) return _clipCache[clipId];
  const def = resolveClip(clipId);
  if (!def) throw new Error(`ClipLoader: unknown clip id "${clipId}"`);
  const gltf = await _loadGltfOnce(def.glb);
  if (!gltf.animations || gltf.animations.length === 0) {
    throw new Error(`ClipLoader: ${def.glb} contains no animations`);
  }
  const clip = def.clip_name
    ? gltf.animations.find((c) => c.name === def.clip_name) || gltf.animations[0]
    : gltf.animations[0];
  // Tag the clip with the manifest id so debugging dumps stay readable.
  clip.userData = { ...(clip.userData || {}), manifest_clip_id: clipId };
  _clipCache[clipId] = clip;
  return clip;
}

/**
 * Resolve a verb to its clip id and ensure the clip is loaded.
 * Returns the AnimationClip (or null when the verb has no clip mapping).
 */
export async function loadClipForVerb(verbId) {
  const verb = resolveVerb(verbId);
  if (!verb || !verb.clip) return null;
  return loadClip(verb.clip);
}

/**
 * Mesh-aware clip loader.
 *
 * In the happy path (mesh has no `bone_name_remap` in the manifest)
 * this is a 100% transparent passthrough to loadClip(clipId) — same
 * cached AnimationClip instance, same identity, same memory footprint.
 *
 * In the BACKUP path (mesh has a Synty→Mixamo remap because the
 * Blender re-parent failed for that character and we shipped Synty's
 * native skeleton), we retarget the Mixamo-authored clip to the
 * Synty bone names ONCE per (clipId, meshId) pair and cache the
 * result forever after.
 *
 * Why a (clipId, meshId) cache key instead of just clipId:
 *   * The same clip (e.g. "sprint") can be retargeted to multiple
 *     non-Mixamo skeletons in the same scene if the user mixes
 *     Synty + Mixamo + future packs. Each target needs its own
 *     retargeted clip with its own track names.
 *   * Per-player AnimationMixer.clipAction(clip) keys actions by
 *     the clip's identity, so caching here keeps action lookups
 *     stable.
 *
 * Retargeting algorithm (THREE.SkeletonUtils.retargetClip):
 *   1. Build a temporary throw-away skinned skeleton at bind pose
 *      from the source clip's bone hierarchy.
 *   2. Sample the clip onto the target skeleton frame-by-frame,
 *      remapping track names through `options.names`.
 *   3. Emit a fresh AnimationClip whose tracks address the target
 *      bone names directly. Mixer plays it natively — no per-frame
 *      retargeting cost.
 *
 * We pass `useFirstFramePosition: true` so the retargeted clip lines
 * up at the source clip's first frame instead of the target's bind
 * pose (avoids a 1-frame T-pose snap on first play).
 *
 * NOTE: retargeting requires the SOURCE clip and the TARGET skeleton
 * to be available at retarget time. Since our source clips
 * (anim_*.glb) always ship a complete Mixamo skeleton in the GLB and
 * the target skeleton is the Synty mesh's loaded skeleton, both are
 * present by the time loadClipForMesh resolves.
 *
 * @param {string} clipId — manifest clip id (e.g. "sprint")
 * @param {string} meshId — manifest mesh id (e.g. "synty_lineman")
 * @returns {Promise<THREE.AnimationClip>}
 */
export async function loadClipForMesh(clipId, meshId) {
  const baseClip = await loadClip(clipId);
  if (!meshId) return baseClip;
  const meshDef = MESHES[meshId];
  // Happy path: no remap declared → return the shared clip instance
  // unmodified. This is the >99% case post-Blender-reparent.
  if (!meshDef || !meshDef.bone_name_remap) return baseClip;
  const cacheKey = `${clipId}::${meshId}`;
  if (_retargetedClipCache[cacheKey]) return _retargetedClipCache[cacheKey];

  // We need a source skeleton root + a target skeleton root for
  // SkeletonUtils.retargetClip. The source comes from the clip's
  // GLB scene (the X-Bot/Mixamo skeleton baked into anim_*.glb).
  // The target comes from the mesh GLB's skinned mesh skeleton.
  const clipDef = resolveClip(clipId);
  const meshFile = resolveMesh(meshId).glb;
  const [clipGltf, meshGltf] = await Promise.all([
    _loadGltfOnce(clipDef.glb),
    _loadGltfOnce(meshFile),
  ]);
  const sourceRoot = _findSkinnedRoot(clipGltf.scene);
  const targetRoot = _findSkinnedRoot(meshGltf.scene);
  if (!sourceRoot || !targetRoot) {
    console.warn(
      `ClipLoader.loadClipForMesh: missing skeleton (src=${!!sourceRoot} tgt=${!!targetRoot}); ` +
      `falling back to un-retargeted clip for mesh "${meshId}"`,
    );
    return baseClip;
  }

  // Build the names map SkeletonUtils expects: { targetBoneName: sourceBoneName }
  // Our manifest stores it as { mixamorigBoneName: syntyBoneName },
  // i.e. { sourceBoneName: targetBoneName } — invert here so callers
  // can keep the human-readable orientation in the manifest.
  const names = {};
  Object.entries(meshDef.bone_name_remap).forEach(([srcName, tgtName]) => {
    names[tgtName] = srcName;
  });

  let retargeted;
  try {
    retargeted = retargetClip(targetRoot, sourceRoot, baseClip, {
      hip: 'mixamorigHips',
      preservePosition: false,
      useFirstFramePosition: true,
      names,
    });
  } catch (err) {
    console.warn(
      `ClipLoader.loadClipForMesh: retargetClip threw for ${cacheKey} — ` +
      `falling back to original clip. Bone map likely incorrect. Error: ${err && err.message}`,
    );
    return baseClip;
  }
  retargeted.name = `${baseClip.name || clipId}__retargeted_${meshId}`;
  retargeted.userData = {
    ...(retargeted.userData || {}),
    manifest_clip_id: clipId,
    retargeted_for_mesh: meshId,
  };
  _retargetedClipCache[cacheKey] = retargeted;
  return retargeted;
}

function _findSkinnedRoot(scene) {
  if (!scene) return null;
  // First preference: a SkinnedMesh (carries `.skeleton.bones` directly,
  // SkeletonUtils.retargetClip's happy path). Mesh GLBs always have
  // one — that's what gets rendered.
  let skinned = null;
  scene.traverse((obj) => {
    if (!skinned && obj.isSkinnedMesh) skinned = obj;
  });
  if (skinned) return skinned;

  // Fallback: animation-only GLBs (anim_*.glb shipping a Mixamo clip)
  // export the skeleton as a bare Object3D + Bone hierarchy WITHOUT a
  // SkinnedMesh — the file is meant to be sampled as keyframe data,
  // not rendered. SkeletonUtils.retargetClip walks the source object's
  // hierarchy via `.traverse(...)` looking up bones by name (it
  // pre-builds a `boneMap`), so any Object3D that REACHES the bones
  // works as the source root.
  //
  // Strategy: walk down from the scene root and return the FIRST
  // Object3D whose subtree contains at least one Bone. For the typical
  // single-armature export from Blender / Mixamo this is the "Armature"
  // node directly under scene root. We don't return scene itself
  // because retargetClip caches by source.uuid and we want a stable
  // identity per skeleton.
  let armature = null;
  for (const child of scene.children || []) {
    let hasBone = false;
    child.traverse((obj) => {
      if (obj.isBone) hasBone = true;
    });
    if (hasBone) {
      armature = child;
      break;
    }
  }
  return armature;
}

/**
 * Pre-load a list of clip ids in parallel. Useful at scene-load time
 * when we want to crossfade without waiting for a network round-trip
 * on the first verb transition.
 */
export function preloadClips(clipIds) {
  return Promise.all(
    (clipIds || [])
      .filter((id, i, arr) => id && arr.indexOf(id) === i) // unique
      .map((id) => loadClip(id).catch((err) => {
        console.warn(`ClipLoader: failed to preload "${id}"`, err && err.message);
        return null;
      }))
  );
}

/**
 * Pre-load every clip referenced by the given verbs.
 * Called from FootballScene3D when a new scene's actors are set, so the
 * clips needed for the play are warm before the keyframes start firing.
 */
export function preloadClipsForVerbs(verbIds) {
  const clipIds = (verbIds || [])
    .map((v) => resolveVerb(v))
    .filter(Boolean)
    .map((v) => v.clip)
    .filter(Boolean);
  return preloadClips(clipIds);
}

/** Diagnostic — which clips have been loaded so far. */
export function loadedClipIds() {
  return Object.keys(_clipCache);
}

/**
 * Test-only — wipe caches between cases so we can simulate fresh loads
 * without restarting the JS runtime.
 */
export function _resetForTests() {
  Object.keys(_clipCache).forEach((k) => delete _clipCache[k]);
  Object.keys(_gltfCache).forEach((k) => delete _gltfCache[k]);
  Object.keys(_gltfPromises).forEach((k) => delete _gltfPromises[k]);
  Object.keys(_retargetedClipCache).forEach((k) => delete _retargetedClipCache[k]);
}
